import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Sparkles, Wand2 } from "lucide-react";

const PROMPT = "A 4-week glow-up course for busy moms";
const DURATIONS = { typing: 2400, drafting: 1900, ready: 4800 };
const NEXT = { typing: "drafting", drafting: "ready", ready: "typing" };

const MODULES = [
  "Week 1 · Reset your routine",
  "Week 2 · Skincare that sticks",
  "Week 3 · Style without shopping",
  "Week 4 · Confidence on camera",
];

const BULLETS = [
  "Names it, prices it, writes the sales page",
  "Builds the full module outline",
  "Publishes to your storefront in one click",
];

export const MzAdvisor = () => {
  const [phase, setPhase] = useState("typing");
  useEffect(() => {
    const t = setTimeout(() => setPhase(NEXT[phase]), DURATIONS[phase]);
    return () => clearTimeout(t);
  }, [phase]);

  return (
    <section className="py-10 sm:py-14">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="relative rounded-[2rem] sm:rounded-[2.5rem] bg-[#0A0A0A] overflow-hidden">
          <div
            aria-hidden
            className="absolute inset-0 pointer-events-none"
            style={{ background: "radial-gradient(circle at 82% 12%, rgba(212,175,55,0.22), transparent 46%), radial-gradient(circle at 8% 90%, rgba(212,175,55,0.1), transparent 40%)" }}
          />
          <div className="relative grid lg:grid-cols-2 gap-12 items-center p-8 sm:p-14 lg:p-20">
            <div>
              <p className="mz-eyebrow !text-[#D4AF37] mb-3">Studio Advisor</p>
              <h2 className="mz-h text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter text-white">
                One sentence in.{" "}
                <span className="mz-gold-text">A finished product out.</span>
              </h2>
              <p className="mt-5 text-[15px] text-white/55 leading-relaxed max-w-md">
                Describe what you want to sell. The Studio Advisor does the rest,
                in seconds, in your brand voice.
              </p>
              <ul className="mt-7 space-y-3.5">
                {BULLETS.map((b) => (
                  <li key={b} className="flex items-center gap-3 text-sm text-white/75">
                    <span className="mz-gold-fill w-5 h-5 rounded-full flex items-center justify-center shrink-0">
                      <Check size={11} className="text-[#1a1206]" strokeWidth={3} />
                    </span>
                    {b}
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <div
                className="rounded-2xl bg-white/[0.04] border border-white/10 backdrop-blur-sm p-5 sm:p-6"
                data-testid="advisor-demo-input"
              >
                <div className="flex items-center gap-2.5 rounded-xl bg-white/[0.06] border border-white/10 px-4 py-3.5">
                  <Wand2 size={14} className="text-[#D4AF37] shrink-0" strokeWidth={2} />
                  <span className="text-[13px] text-white/85 truncate">
                    {phase === "typing" ? (
                      <span key="typing" className="mz-type">{PROMPT}</span>
                    ) : (
                      PROMPT
                    )}
                  </span>
                </div>

                <div className="mt-4 min-h-[210px]" data-testid="advisor-demo-output">
                  <AnimatePresence mode="wait">
                    {phase === "ready" ? (
                      <motion.div
                        key="ready"
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.4 }}
                        className="rounded-xl bg-white/[0.05] border border-[#D4AF37]/25 p-5"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <p className="mz-h text-[15px] font-semibold text-white">The 4-Week Glow-Up Program</p>
                          <span className="mz-h text-[15px] font-bold mz-gold-text shrink-0">$49</span>
                        </div>
                        <div className="mt-3.5 space-y-2">
                          {MODULES.map((m, i) => (
                            <motion.p
                              key={m}
                              initial={{ opacity: 0, x: -8 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: 0.15 + i * 0.12 }}
                              className="flex items-center gap-2 text-[12px] text-white/60"
                            >
                              <Check size={11} className="text-[#D4AF37] shrink-0" strokeWidth={2.6} />
                              {m}
                            </motion.p>
                          ))}
                        </div>
                        <motion.span
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 0.7 }}
                          className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/30 px-3 py-1.5 text-[10.5px] font-semibold"
                          style={{ color: "#F0D98C" }}
                        >
                          <Sparkles size={10} strokeWidth={2.2} />
                          Ready to sell · Published to your storefront
                        </motion.span>
                      </motion.div>
                    ) : (
                      <motion.div
                        key="drafting"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="rounded-xl bg-white/[0.03] border border-white/10 p-5 space-y-3"
                      >
                        <p className="flex items-center gap-2 text-[11px] font-medium text-[#D4AF37]/90">
                          <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] animate-pulse" />
                          {phase === "drafting" ? "Drafting your product..." : "Waiting for your idea..."}
                        </p>
                        <div className="mz-skel h-4 w-3/4" />
                        <div className="mz-skel h-3 w-1/3" />
                        <div className="mz-skel h-3 w-5/6" />
                        <div className="mz-skel h-3 w-2/3" />
                        <div className="mz-skel h-3 w-4/6" />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
              <p className="mt-4 text-center text-[11px] text-white/30">
                Live drafts run inside your dashboard once the Hub is enabled.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
